import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// pwa-links-client-mod -- 管理面板
//
// PWA 链接分类管理、CRUD 管理界面、批量导入。
// 注册到 admin-client-mod 侧边栏。
// ============================================================================
import * as React from 'react';
import { useClientServiceByTag } from '@hvsin/mod-client-runtime';
import { PwaLinksEditorDialog } from './pwa-links-editor-dialog.js';
import { ConfirmDialog } from './confirm-dialog.js';
// 批量导入 JSON 示例
const BATCH_IMPORT_EXAMPLE = `[
  {
    "name": "Google Maps",
    "url": "https://maps.google.com",
    "description": "全球地图与导航",
    "icon": "",
    "category": "地图出行",
    "tags": ["地图", "导航"],
    "order": 100
  },
  {
    "name": "Spotify",
    "url": "https://open.spotify.com",
    "description": "全球最大音乐流媒体",
    "category": "音乐音频",
    "tags": ["音乐", "流媒体"]
  }
]`;
export function PwaLinksAdminPanel() {
    const apiClient = useClientServiceByTag('pwaLinksApiClient');
    const [links, setLinks] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    // Editor state
    const [editingLink, setEditingLink] = React.useState(null);
    const [showCreateDialog, setShowCreateDialog] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [togglingId, setTogglingId] = React.useState(null);
    // Batch import state
    const [showImportPanel, setShowImportPanel] = React.useState(false);
    const [importJson, setImportJson] = React.useState('');
    const [importMode, setImportMode] = React.useState('skip');
    const [importing, setImporting] = React.useState(false);
    const [importResult, setImportResult] = React.useState(null);
    const [importError, setImportError] = React.useState(null);
    const fileInputRef = React.useRef(null);
    const fetchData = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [linksRes, catsRes] = await Promise.all([
                apiClient.getAllAdmin({ pageSize: 100 }),
                apiClient.getCategories(),
            ]);
            setLinks(linksRes.data);
            setCategories(catsRes);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : '加载失败');
        }
        finally {
            setLoading(false);
        }
    }, []);
    React.useEffect(() => {
        fetchData();
    }, [fetchData]);
    const handleToggle = async (id) => {
        setTogglingId(id);
        try {
            await apiClient.toggleLink(id);
            await fetchData();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : '操作失败');
        }
        finally {
            setTogglingId(null);
        }
    };
    const handleDelete = async () => {
        if (!deleteTarget)
            return;
        try {
            await apiClient.deleteLink(deleteTarget._id);
            setDeleteTarget(null);
            await fetchData();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : '删除失败');
            setDeleteTarget(null);
        }
    };
    const handleSave = async (data) => {
        const tags = data.tags.split(',').map(t => t.trim()).filter(Boolean);
        const payload = {
            name: data.name,
            url: data.url,
            description: data.description,
            icon: data.icon,
            category: data.category,
            tags,
            order: data.order,
        };
        if (editingLink) {
            await apiClient.updateLink(editingLink._id, payload);
        }
        else {
            await apiClient.createLink(payload);
        }
        setEditingLink(null);
        setShowCreateDialog(false);
        await fetchData();
    };
    // 批量导入处理
    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target?.result;
            if (typeof text === 'string') {
                setImportJson(text);
                setImportResult(null);
                setImportError(null);
            }
        };
        reader.readAsText(file);
        // 重置 input 以便同一文件可以重复选择
        if (fileInputRef.current)
            fileInputRef.current.value = '';
    };
    const handleBatchImport = async () => {
        setImportError(null);
        setImportResult(null);
        if (!importJson.trim()) {
            setImportError('请先输入或上传 JSON 数据');
            return;
        }
        let items;
        try {
            items = JSON.parse(importJson);
        }
        catch {
            setImportError('JSON 格式错误，请检查输入');
            return;
        }
        if (!Array.isArray(items)) {
            setImportError('JSON 数据必须是数组格式');
            return;
        }
        setImporting(true);
        try {
            const result = await apiClient.batchImport({
                mode: importMode,
                items,
            });
            setImportResult(result);
            // 导入成功后刷新列表
            if (result.created > 0 || result.updated > 0) {
                await fetchData();
            }
        }
        catch (err) {
            setImportError(err instanceof Error ? err.message : '导入失败');
        }
        finally {
            setImporting(false);
        }
    };
    const loadExample = () => {
        setImportJson(BATCH_IMPORT_EXAMPLE);
        setImportResult(null);
        setImportError(null);
    };
    const resetImport = () => {
        setImportJson('');
        setImportMode('skip');
        setImportResult(null);
        setImportError(null);
    };
    return (_jsxs("div", { style: { padding: 24 }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }, children: [_jsxs("div", { children: [_jsx("h2", { style: { margin: 0, fontSize: 18, fontWeight: 600, color: '#111827' }, children: "PWA \u5E94\u7528\u7BA1\u7406" }), _jsxs("p", { style: { margin: '4px 0 0', fontSize: 13, color: '#6b7280' }, children: ["\u5171 ", links.length, " \u4E2A\u5E94\u7528\uFF0C", links.filter(l => l.enabled).length, " \u4E2A\u5DF2\u542F\u7528"] })] }), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [_jsx("button", { onClick: () => {
                                    setShowImportPanel(!showImportPanel);
                                    if (!showImportPanel) {
                                        resetImport();
                                    }
                                }, style: {
                                    padding: '8px 16px',
                                    borderRadius: 8,
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: showImportPanel ? '#f3f4f6' : '#fff',
                                    color: '#374151',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                }, children: "\u6279\u91CF\u5BFC\u5165" }), _jsx("button", { onClick: () => setShowCreateDialog(true), style: {
                                    padding: '8px 16px',
                                    borderRadius: 8,
                                    border: 'none',
                                    backgroundColor: '#4f46e5',
                                    color: '#fff',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                }, children: "+ \u65B0\u589E\u5E94\u7528" })] })] }), error && (_jsx("div", { style: {
                    padding: '10px 14px',
                    marginBottom: 16,
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 8,
                    color: '#dc2626',
                    fontSize: 13,
                }, children: error })), showImportPanel && (_jsxs("div", { style: {
                    marginBottom: 20,
                    padding: 20,
                    borderRadius: 12,
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#fafafa',
                }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }, children: [_jsx("h3", { style: { margin: 0, fontSize: 15, fontWeight: 600, color: '#111827' }, children: "\u6279\u91CF\u5BFC\u5165 PWA \u5E94\u7528" }), _jsx("button", { onClick: loadExample, style: {
                                    padding: '4px 12px',
                                    borderRadius: 6,
                                    border: '1px solid #d1d5db',
                                    backgroundColor: '#fff',
                                    fontSize: 12,
                                    color: '#6b7280',
                                    cursor: 'pointer',
                                }, children: "\u52A0\u8F7D\u793A\u4F8B" })] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }, children: [_jsx("span", { style: { fontSize: 13, color: '#6b7280' }, children: "\u91CD\u590D\u9879\u5904\u7406\uFF1A" }), _jsxs("label", { style: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }, children: [_jsx("input", { type: "radio", name: "importMode", checked: importMode === 'skip', onChange: () => setImportMode('skip') }), "\u8DF3\u8FC7\u5DF2\u5B58\u5728"] }), _jsxs("label", { style: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }, children: [_jsx("input", { type: "radio", name: "importMode", checked: importMode === 'overwrite', onChange: () => setImportMode('overwrite') }), "\u8986\u76D6\u5DF2\u5B58\u5728"] })] }), _jsxs("div", { style: { marginBottom: 12 }, children: [_jsx("input", { ref: fileInputRef, type: "file", accept: ".json,application/json", onChange: handleFileUpload, style: { display: 'none' }, id: "batch-import-file" }), _jsx("label", { htmlFor: "batch-import-file", style: {
                                    display: 'inline-block',
                                    padding: '6px 14px',
                                    borderRadius: 6,
                                    border: '1px dashed #d1d5db',
                                    backgroundColor: '#fff',
                                    fontSize: 13,
                                    color: '#4b5563',
                                    cursor: 'pointer',
                                }, children: "\u4E0A\u4F20 JSON \u6587\u4EF6" }), _jsx("span", { style: { marginLeft: 10, fontSize: 12, color: '#9ca3af' }, children: "\u6216\u76F4\u63A5\u7C98\u8D34 JSON \u5230\u4E0B\u65B9\u6587\u672C\u6846" })] }), _jsx("textarea", { value: importJson, onChange: (e) => { setImportJson(e.target.value); setImportResult(null); setImportError(null); }, placeholder: "\u7C98\u8D34 JSON \u6570\u636E\uFF0C\u683C\u5F0F\uFF1A[{ name, url, description?, icon?, category?, tags?, order? }]", style: {
                            width: '100%',
                            minHeight: 160,
                            padding: '10px 12px',
                            borderRadius: 8,
                            border: '1px solid #d1d5db',
                            fontSize: 12,
                            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                            lineHeight: 1.5,
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            outline: 'none',
                        }, onFocus: (e) => { e.currentTarget.style.borderColor = '#4f46e5'; }, onBlur: (e) => { e.currentTarget.style.borderColor = '#d1d5db'; } }), _jsxs("div", { style: { display: 'flex', gap: 8, marginTop: 12 }, children: [_jsx("button", { onClick: handleBatchImport, disabled: importing || !importJson.trim(), style: {
                                    padding: '8px 20px',
                                    borderRadius: 8,
                                    border: 'none',
                                    backgroundColor: importing ? '#9ca3af' : '#4f46e5',
                                    color: '#fff',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: importing ? 'not-allowed' : 'pointer',
                                }, children: importing ? '导入中...' : '执行导入' }), _jsx("button", { onClick: resetImport, disabled: importing, style: {
                                    padding: '8px 20px',
                                    borderRadius: 8,
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#fff',
                                    color: '#6b7280',
                                    fontSize: 13,
                                    cursor: importing ? 'not-allowed' : 'pointer',
                                }, children: "\u6E05\u7A7A" })] }), importError && (_jsx("div", { style: {
                            marginTop: 12,
                            padding: '10px 14px',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: 8,
                            color: '#dc2626',
                            fontSize: 13,
                        }, children: importError })), importResult && (_jsxs("div", { style: {
                            marginTop: 12,
                            padding: '14px 16px',
                            backgroundColor: importResult.failed > 0 ? '#fffbeb' : '#ecfdf5',
                            border: `1px solid ${importResult.failed > 0 ? '#fde68a' : '#a7f3d0'}`,
                            borderRadius: 8,
                            fontSize: 13,
                        }, children: [_jsx("div", { style: { fontWeight: 600, marginBottom: 8, color: importResult.failed > 0 ? '#92400e' : '#065f46' }, children: "\u5BFC\u5165\u5B8C\u6210" }), _jsxs("div", { style: { display: 'flex', gap: 20, color: '#374151' }, children: [_jsxs("span", { children: ["\u603B\u8BA1: ", _jsx("strong", { children: importResult.total })] }), _jsxs("span", { style: { color: '#059669' }, children: ["\u65B0\u5EFA: ", _jsx("strong", { children: importResult.created })] }), _jsxs("span", { style: { color: '#2563eb' }, children: ["\u66F4\u65B0: ", _jsx("strong", { children: importResult.updated })] }), _jsxs("span", { style: { color: '#d97706' }, children: ["\u8DF3\u8FC7: ", _jsx("strong", { children: importResult.skipped })] }), _jsxs("span", { style: { color: '#dc2626' }, children: ["\u5931\u8D25: ", _jsx("strong", { children: importResult.failed })] })] }), importResult.errors.length > 0 && (_jsxs("div", { style: { marginTop: 10 }, children: [_jsx("div", { style: { fontWeight: 500, marginBottom: 4, color: '#92400e' }, children: "\u9519\u8BEF\u8BE6\u60C5\uFF1A" }), _jsx("div", { style: {
                                            maxHeight: 120,
                                            overflowY: 'auto',
                                            padding: '8px 10px',
                                            backgroundColor: '#fff',
                                            borderRadius: 6,
                                            border: '1px solid #fde68a',
                                            fontSize: 12,
                                            fontFamily: 'ui-monospace, monospace',
                                        }, children: importResult.errors.map((err, i) => (_jsxs("div", { style: { marginBottom: 2, color: '#dc2626' }, children: ["[", err.index, "] ", err.name, ": ", err.reason] }, i))) })] }))] }))] })), loading ? (_jsx("div", { style: { padding: 40, textAlign: 'center', color: '#9ca3af' }, children: "\u52A0\u8F7D\u4E2D..." })) : (_jsx("div", { style: { borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }, children: _jsxs("table", { style: { width: '100%', borderCollapse: 'collapse', fontSize: 13 }, children: [_jsx("thead", { children: _jsxs("tr", { style: { backgroundColor: '#f9fafb' }, children: [_jsx("th", { style: thStyle, children: "\u5E94\u7528" }), _jsx("th", { style: thStyle, children: "\u5206\u7C7B" }), _jsx("th", { style: thStyle, children: "\u6392\u5E8F" }), _jsx("th", { style: thStyle, children: "\u70B9\u51FB" }), _jsx("th", { style: thStyle, children: "\u72B6\u6001" }), _jsx("th", { style: { ...thStyle, textAlign: 'right' }, children: "\u64CD\u4F5C" })] }) }), _jsx("tbody", { children: links.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 6, style: { padding: 40, textAlign: 'center', color: '#9ca3af' }, children: "\u6682\u65E0\u5E94\u7528\uFF0C\u70B9\u51FB\"\u65B0\u589E\u5E94\u7528\"\u5F00\u59CB\u6DFB\u52A0" }) })) : (links.map(link => (_jsxs("tr", { style: { borderTop: '1px solid #f3f4f6' }, children: [_jsx("td", { style: tdStyle, children: _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 10 }, children: [link.icon ? (_jsx("img", { src: link.icon, alt: "", width: 32, height: 32, style: { borderRadius: 8, objectFit: 'cover' }, onError: (e) => { e.currentTarget.style.display = 'none'; } })) : null, _jsxs("div", { children: [_jsx("div", { style: { fontWeight: 500, color: '#111827' }, children: link.name }), _jsx("div", { style: { color: '#9ca3af', fontSize: 12, marginTop: 2 }, children: link.url })] })] }) }), _jsx("td", { style: tdStyle, children: _jsx("span", { style: {
                                                padding: '2px 8px',
                                                borderRadius: 999,
                                                backgroundColor: '#eff6ff',
                                                color: '#2563eb',
                                                fontSize: 12,
                                            }, children: link.category || '-' }) }), _jsx("td", { style: tdStyle, children: link.order }), _jsx("td", { style: tdStyle, children: link.clickCount }), _jsx("td", { style: tdStyle, children: _jsx("span", { style: {
                                                padding: '2px 8px',
                                                borderRadius: 999,
                                                backgroundColor: link.enabled ? '#ecfdf5' : '#fef2f2',
                                                color: link.enabled ? '#059669' : '#dc2626',
                                                fontSize: 12,
                                            }, children: link.enabled ? '启用' : '禁用' }) }), _jsx("td", { style: { ...tdStyle, textAlign: 'right' }, children: _jsxs("div", { style: { display: 'flex', gap: 6, justifyContent: 'flex-end' }, children: [_jsx("button", { onClick: () => setEditingLink(link), style: smallBtnStyle, children: "\u7F16\u8F91" }), _jsx("button", { onClick: () => handleToggle(link._id), disabled: togglingId === link._id, style: {
                                                        ...smallBtnStyle,
                                                        backgroundColor: link.enabled ? '#fef2f2' : '#ecfdf5',
                                                        color: link.enabled ? '#dc2626' : '#059669',
                                                    }, children: link.enabled ? '禁用' : '启用' }), _jsx("button", { onClick: () => setDeleteTarget(link), style: { ...smallBtnStyle, backgroundColor: '#fef2f2', color: '#dc2626' }, children: "\u5220\u9664" })] }) })] }, link._id)))) })] }) })), (showCreateDialog || editingLink) && (_jsx(PwaLinksEditorDialog, { link: editingLink, categories: categories, onSave: handleSave, onClose: () => { setShowCreateDialog(false); setEditingLink(null); } })), deleteTarget && (_jsx(ConfirmDialog, { title: "\u786E\u8BA4\u5220\u9664", message: `确定要删除「${deleteTarget.name}」吗？此操作不可撤销。`, onConfirm: handleDelete, onCancel: () => setDeleteTarget(null) }))] }));
}
const thStyle = {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};
const tdStyle = {
    padding: '12px 14px',
    verticalAlign: 'middle',
    color: '#374151',
};
const smallBtnStyle = {
    padding: '4px 10px',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    fontSize: 12,
    color: '#4b5563',
    cursor: 'pointer',
};
//# sourceMappingURL=pwa-links-admin-panel.js.map